import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import Downshift from "downshift";
import { matchSorter } from "match-sorter";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import useSWR from "swr";
import useSWRInfinite from "swr/infinite";
import { useFormContext, useController } from "react-hook-form";
import { useProfile } from "./DataHooks/useProfile";
import useDebounce from "./UseDebounce";
import {
  escapeRegExp,
  comboRegex,
  getOrderItemSearchStartDate,
} from "../helpers/methods";
import { fetcher } from "../helpers/fetchHandleError";
import { useShowContext } from "react-admin";
import { Warning } from "@mui/icons-material";
import { useIsBusinessPlan } from "../helpers/subscriptionPlan";
import { useTheme } from "@mui/material";

const PAGE_SIZE = 20;

const itemToString = (item) => {
  if (item && item.description) {
    const spacelessDescription = item.description.replace(/\s/g, " ").trim();
    const matches = comboRegex.exec(spacelessDescription);
    if (matches) {
      const [fullString, productDescription, quantity] = matches;
      return productDescription.trim();
    } else return item.description;
  } else if (item) {
    return item;
  }
  return "";
};

const keywordToString = (keyword) => {
  if (keyword && keyword.key) {
    //return keyword
    return keyword.key;
  } else if (keyword) {
    return keyword;
  }
  return "";
};

export const DownshiftInputDescription = ({
  // input,
  meta,
  placeholder,
  label,
  // items,
  ...rest
}) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const theme = useTheme();
  const { field } = useController({ name: "item" });
  const { setValue } = useFormContext();
  const debouncedSearchTerm = useDebounce(field.value, 800);
  const { profile } = useProfile();
  const trimmedSearchTerm = debouncedSearchTerm && debouncedSearchTerm.trim();

  const target =
    profile &&
    profile.page &&
    profile.page._id &&
    trimmedSearchTerm &&
    trimmedSearchTerm.length > 1
      ? `${
          process.env.REACT_APP_CMS_HOST
        }/orderItems/search?descriptionSearch_containss=${encodeURIComponent(
          trimmedSearchTerm.toUpperCase()
        )}&page=${
          profile.page._id
        }&_sort=updatedAt:DESC&_start=0&_limit=15&createdAt_gte=${getOrderItemSearchStartDate().toISOString()}`
      : null;
  const { data, error, isValidating } = useSWR(target);

  const items = data
    ? data.filter(
        (v, i, a) => a.findIndex((t) => t.description === v.description) === i
      )
    : [];
  return (
    <Downshift
      isOpen={menuIsOpen}
      onOuterClick={() => setMenuIsOpen(false)}
      onSelect={(selectedItem) => {
        if (selectedItem) {
          const { price = 0, description = "", weight, cost } = selectedItem;
          const spacelessDescription = description.replace(/\s/g, " ").trim();
          const matches = comboRegex.exec(spacelessDescription);
          const priceNumber = Number(price);
          const weightNumber = Number(weight);
          const costNumber = Number(cost);

          if (matches) {
            const [fullString, productDescription, quantity] = matches;
            setValue("item", productDescription.trim());
            setValue("quantity", parseInt(quantity));
          }
          if (!isNaN(priceNumber)) {
            setValue("price", priceNumber);
          }
          if (!isNaN(weightNumber)) {
            setValue("weight", weightNumber);
          }
          if (!isNaN(costNumber)) {
            setValue("cost", costNumber);
          }
        }
        setMenuIsOpen(false);
      }}
      itemToString={itemToString}
      //selectedItem={input.value}
    >
      {({
        getInputProps,
        getItemProps,
        getLabelProps,
        isOpen,
        inputValue,
        highlightedIndex,
        selectedItem,
      }) => {
        const filteredItems = matchSorter(items, field.value, {
          keys: ["description"],
          maxRanking: matchSorter.rankings.STARTS_WITH,
        });
        //basically we use Downshift to render the selections only. Inputs are handled by react hook form
        return (
          <div className="downshift" style={{ position: "relative" }}>
            <TextField
              variant="filled"
              required
              fullWidth
              label={label}
              InputProps={{
                endAdornment: isValidating ? (
                  <InputAdornment position="end">
                    <CircularProgress size="1rem" />
                  </InputAdornment>
                ) : null,
              }}
              onChange={(e) => {
                setMenuIsOpen(true);
                field.onChange(e.target.value);
              }}
              value={field.value}
            />
            {isOpen && filteredItems.length > 0 && (
              <div
                className="downshift-options"
                style={{
                  background:
                    theme.palette.mode === "dark"
                      ? theme.palette.background.paper
                      : "#fff",
                  position: "absolute",
                  top: "100%",
                  left: 15,
                  right: 0,
                  zIndex: 4,
                  border: `1px solid ${
                    theme.palette.mode === "dark"
                      ? theme.palette.divider
                      : "#ccc"
                  }`,
                  borderRadius: 4,
                }}
              >
                {filteredItems.map(
                  ({ description, price, weight, cost }, index) => (
                    <div
                      {...getItemProps({
                        key: `${description}-${index}`,
                        index,
                        item: { description, price, weight, cost },
                        style: {
                          backgroundColor:
                            highlightedIndex === index
                              ? theme.palette.action.hover
                              : "inherit",
                          border:
                            highlightedIndex === index
                              ? `1px solid ${theme.palette.divider}`
                              : "1px solid transparent",
                          borderRadius: 4,
                          padding: "8px",
                          listStyle: "none",
                          margin: 0,
                          color: theme.palette.text.primary,
                        },
                      })}
                    >
                      {`${description} (price: ${price})`}
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        );
      }}
    </Downshift>
  );
};

export const DownshiftKeyword = ({ meta, placeholder, label }) => {
  const { field } = useController({ name: "key" });
  const theme = useTheme();

  const { setValue } = useFormContext();
  const { profile } = useProfile();
  const { record } = useShowContext();
  const isBusinessPlan = useIsBusinessPlan();
  const { contact: { vipTier: contactVipTier } = { vipTier: 0 } } =
    record ?? {};
  const searchInactive =
    profile &&
    profile.params &&
    profile.params.enableManualAddItemInactiveKeyword;

  const debouncedSearchTerm = useDebounce(field.value, 800);

  const getKey = (pageIndex, previousPageData) => {
    if (previousPageData && !previousPageData.length) return null; // reached the end

    return `${
      process.env.REACT_APP_CMS_HOST
    }/keywords/?key_containss=${encodeURIComponent(
      escapeRegExp(debouncedSearchTerm)
    )}&page=${profile.page._id}&_sort=key:ASC&_start=${
      pageIndex * PAGE_SIZE
    }&_limit=${PAGE_SIZE}${searchInactive ? "" : "&isActive=true"}`;
  };

  const { data, error, size, setSize, isValidating } = useSWRInfinite(
    getKey,
    fetcher,
    {
      revalidateFirstPage: false,
      revalidateAll: false, // Prevent revalidating all pages on mount
      shouldRetryOnError: false, // Prevent auto-retry on error
    }
  );

  const keywords = useMemo(() => (data ? data.flat() : []), [data]);

  const handleIntersection = useCallback(
    (entries) => {
      const target = entries[0];
      if (target.isIntersecting && size > 0 && !error) {
        const lastPageData = data[size - 1];
        if (lastPageData && lastPageData.length > 0) {
          setSize(size + 1);
        }
      }
    },
    [size, error, data, setSize]
  );

  const infiniteScrollRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    });

    if (infiniteScrollRef.current) {
      observer.observe(infiniteScrollRef.current);
    }

    return () => {
      if (infiniteScrollRef.current) {
        observer.unobserve(infiniteScrollRef.current);
      }
    };
  }, [handleIntersection]);

  return (
    <Downshift
      onInputValueChange={(inputValue) => {
        field.onChange(inputValue.toUpperCase());
      }}
      onSelect={(selectedItem) => {
        if (selectedItem) {
          const { description = "", id = "" } = selectedItem;
          if (id !== "") {
            setValue("keyword", {
              ...selectedItem,
            });
          }
          if (description !== "") {
            setValue("item", `${description}`);
          }
        }
      }}
      itemToString={keywordToString}
    >
      {({
        getInputProps,
        getItemProps,
        getLabelProps,
        isOpen,
        inputValue,
        highlightedIndex,
        selectedItem,
        getMenuProps,
      }) => {
        const filteredKeywords = matchSorter(keywords, inputValue, {
          keys: ["key"],
          maxRanking: matchSorter.rankings.STARTS_WITH,
        });

        return (
          <div className="downshift" style={{ position: "relative" }}>
            <TextField
              variant="filled"
              required
              fullWidth
              placeholder="A001"
              label={label}
              InputProps={{
                endAdornment: isValidating ? (
                  <InputAdornment position="end">
                    <CircularProgress size="1rem" />
                  </InputAdornment>
                ) : null,
              }}
              {...getInputProps({
                name: field.name,
                placeholder,
              })}
            />

            {selectedItem &&
              isBusinessPlan &&
              selectedItem.vipTier > contactVipTier && (
                <div
                  style={{
                    color: "red",
                    marginTop: "8px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Warning style={{ marginRight: "4px" }} />
                  The contact's VIP tier is lower than the selected item's
                  requirement. Please review your selection before proceeding.
                </div>
              )}
            {isOpen && filteredKeywords.length > 0 && (
              <ul
                className="downshift-options"
                {...getMenuProps()}
                style={{
                  background:
                    theme.palette.mode === "dark"
                      ? theme.palette.background.paper
                      : "#fff",
                  position: "absolute",
                  top: "100%",
                  left: 15,
                  right: 0,
                  zIndex: 4,
                  maxHeight: "150px",
                  overflowY: "auto",
                  margin: 0,
                  padding: 0,
                  border: `1px solid ${
                    theme.palette.mode === "dark"
                      ? theme.palette.divider
                      : "#ccc"
                  }`,
                  borderRadius: 4,
                }}
              >
                {filteredKeywords.map((item, index) => (
                  <li
                    {...getItemProps({
                      key: `${item.description}-${index}`,
                      index,
                      item,
                      style: {
                        backgroundColor:
                          highlightedIndex === index
                            ? theme.palette.action.hover
                            : "inherit",
                        border:
                          highlightedIndex === index
                            ? `1px solid ${theme.palette.divider}`
                            : "1px solid transparent",
                        borderRadius: 4,
                        fontWeight:
                          selectedItem === item.key ? "bold" : "normal",
                        padding: "8px",
                        listStyle: "none",
                        margin: 0,
                        color: theme.palette.text.primary,
                      },
                    })}
                  >
                    {`${index + 1}: ${item.key}${
                      searchInactive && item.isActive
                        ? "(Active)"
                        : searchInactive && !item.isActive
                        ? "(Inactive)"
                        : ""
                    }: ${item.description} (price: ${item.price})`}
                  </li>
                ))}
                <div ref={infiniteScrollRef}>
                  <p
                    style={{
                      borderTop: `1px solid ${
                        theme.palette.mode === "dark"
                          ? theme.palette.divider
                          : "#ccc"
                      }`,
                      padding: "8px",
                      fontStyle: "italic",
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {isValidating
                      ? `Loading...`
                      : `Yay, you have viewed all the ${filteredKeywords.length} keywords`}
                  </p>
                </div>
              </ul>
            )}
          </div>
        );
      }}
    </Downshift>
  );
};
